import React from "react";
import {
  FaPlaneDeparture,
  FaFileContract,
  FaTasks,
  FaQuestionCircle,
  FaDollarSign,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaUser,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";

const CalendarAgendaView = ({ events, onSelectEvent, onDismissReminder }) => {
  if (!events || events.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", background: "#ffffff", borderRadius: 12, border: "1px solid #e2e8f0" }}>
        <FaCalendarCheck style={{ fontSize: 42, color: "#94a3b8", marginBottom: 12 }} />
        <h3 style={{ margin: "0 0 6px 0", color: "#334155", fontSize: 16 }}>No scheduled events or reminders</h3>
        <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
          No records match your active search or filter selection.
        </p>
      </div>
    );
  }

  // Sort events chronologically by start_time
  const sorted = [...events].sort((a, b) => {
    const da = new Date(a.start_time || 0);
    const db = new Date(b.start_time || 0);
    return da - db;
  });

  // Group by date string (e.g. "Monday, Nov 3, 2026")
  const groups = {};
  sorted.forEach((evt) => {
    const d = new Date(evt.start_time);
    const label = !isNaN(d.getTime())
      ? d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "short", day: "numeric" })
      : "Undated Events";

    if (!groups[label]) groups[label] = [];
    groups[label].push(evt);
  });

  const getEventBadgeClass = (evt) => {
    const type = (evt.event_type || "").toLowerCase();
    const source = (evt.module_source || "").toLowerCase();

    if (source === "sales" || type.includes("flight")) return "badge-flight";
    if (source === "documents" || type.includes("expiry")) return "badge-expiry";
    if (source === "tasks" || type.includes("task")) return "badge-task";
    if (source === "inquiries" || type.includes("follow")) return "badge-followup";
    if (source === "invoices" || type.includes("payment")) return "badge-payment";
    return "badge-meeting";
  };

  const getEventIcon = (evt) => {
    const type = (evt.event_type || "").toLowerCase();
    const source = (evt.module_source || "").toLowerCase();

    if (source === "sales" || type.includes("flight")) return <FaPlaneDeparture />;
    if (source === "documents" || type.includes("expiry")) return <FaFileContract />;
    if (source === "tasks" || type.includes("task")) return <FaTasks />;
    if (source === "inquiries" || type.includes("follow")) return <FaQuestionCircle />;
    if (source === "invoices" || type.includes("payment")) return <FaDollarSign />;
    return <FaCalendarCheck />;
  };

  const formatEventTime = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="agenda-view-container">
      {Object.entries(groups).map(([dateLabel, items]) => (
        <div key={dateLabel} className="agenda-date-group">
          <div className="agenda-date-header">
            <span>{dateLabel}</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>
              {items.length} {items.length === 1 ? "item" : "items"}
            </span>
          </div>

          <div>
            {items.map((evt) => (
              <div
                key={evt.id}
                className="agenda-event-row"
                onClick={() => onSelectEvent(evt)}
              >
                <div className="agenda-time-col">
                  {evt.is_all_day ? (
                    <span style={{ color: "#2563eb", fontWeight: 700 }}>ALL DAY</span>
                  ) : (
                    <>
                      <span>{formatEventTime(evt.start_time)}</span>
                      {evt.end_time && (
                        <span className="agenda-time-sub">to {formatEventTime(evt.end_time)}</span>
                      )}
                    </>
                  )}
                </div>

                <div className="agenda-main-col">
                  <div className="agenda-title">
                    <span className={`event-type-badge ${getEventBadgeClass(evt)}`}>
                      {getEventIcon(evt)}
                      {evt.event_type}
                    </span>
                    <span>{evt.title}</span>
                  </div>

                  <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#64748b", flexWrap: "wrap", marginTop: 4 }}>
                    {evt.client_name && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <FaUser style={{ fontSize: 11 }} /> {evt.client_name}
                      </span>
                    )}
                    {evt.location && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <FaMapMarkerAlt style={{ fontSize: 11 }} /> {evt.location}
                      </span>
                    )}
                    {evt.reminder_minutes_before !== undefined && evt.reminder_minutes_before !== null && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <FaClock style={{ fontSize: 11 }} />
                        Alert: {evt.reminder_minutes_before}m before
                      </span>
                    )}
                  </div>

                  {evt.description && (
                    <div className="agenda-desc" style={{ marginTop: 4 }}>
                      {evt.description.length > 120
                        ? `${evt.description.substring(0, 120)}...`
                        : evt.description}
                    </div>
                  )}
                </div>

                <div className="agenda-meta-col">
                  {evt.priority && (
                    <span
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 4,
                        backgroundColor:
                          evt.priority === "urgent"
                            ? "#fee2e2"
                            : evt.priority === "high"
                            ? "#ffedd5"
                            : "#f1f5f9",
                        color:
                          evt.priority === "urgent"
                            ? "#dc2626"
                            : evt.priority === "high"
                            ? "#c2410c"
                            : "#475569",
                      }}
                    >
                      {evt.priority}
                    </span>
                  )}

                  {onDismissReminder && (
                    <button
                      type="button"
                      className="calendar-secondary-btn"
                      style={{ padding: "5px 10px", fontSize: 12 }}
                      title="Acknowledge / Dismiss Reminder"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismissReminder(evt.id);
                      }}
                    >
                      <FaCheckCircle style={{ color: "#10b981" }} /> Done
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CalendarAgendaView;
